import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import './styles/global.css';
import styles from './App.module.css';

import { Login } from './pages/Login/Login';
import { LinkedInCallback } from './pages/Login/LinkedInCallback';
import { ResetPasswordPage } from './pages/Login/ResetPasswordPage';
import { ProjectSelection } from './pages/ProjectSelection/ProjectSelection';
import { Header } from './components/layout/Header/Header';
import { Sidebar } from './components/layout/Sidebar/Sidebar';
import { DashboardModule } from './components/dashboard/DashboardModule';
import { ProjectFlowNav } from './components/flow/ProjectFlowNav';
import { SentimentModule } from './components/sentiment/SentimentModule';
import { PersonasModule } from './components/personas/PersonasModule';
import { StrategyModule } from './components/strategy/StrategyModule';
import { MiddelenModule } from './components/middelen/MiddelenModule';
import { WerkplanningModule } from './components/werkplanning/WerkplanningModule';
import { VragenModule } from './components/vragen/VragenModule';
import { RelatiebeheerModule } from './components/relatiebeheer/RelatiebeheerModule';
import { GebiedsanalyseModule } from './components/gebiedsanalyse/GebiedsanalyseModule';
import { AdminLayout } from './pages/AdminDashboard/AdminLayout';
import { CompaniesPage } from './pages/AdminDashboard/CompaniesPage';
import { CompanyDetailPage } from './pages/AdminDashboard/CompanyDetailPage';
import { ProjectsPage } from './pages/AdminDashboard/ProjectsPage';
import { ProjectDetailPage } from './pages/AdminDashboard/ProjectDetailPage';
import { UsersPage } from './pages/AdminDashboard/UsersPage';
import { UserFormPage } from './pages/AdminDashboard/UserFormPage';
import { ProjectEditPage } from './pages/AdminDashboard/ProjectEditPage';
import { CompanyEditPage } from './pages/AdminDashboard/CompanyEditPage';
import { DataSourcesPage } from './pages/AdminDashboard/DataSourcesPage';
import { AiUsagePage } from './pages/AdminDashboard/AiUsagePage';
import { PromptsPage } from './pages/AdminDashboard/PromptsPage';
import { ProjectWebsiteModule } from './components/projectwebsite/ProjectWebsiteModule';
import { PublicSiteLayout } from './components/public-site/PublicSiteLayout';
import { IssueRadarModule } from './components/issue-radar/IssueRadarModule';
import { ParticipationModule } from './components/participation/ParticipationModule';
import { ReportsModule } from './components/reports/ReportsModule';
import { StakeholderNetworkModule } from './components/stakeholder-network/StakeholderNetworkModule';
import { PermitsModule } from './components/permits/PermitsModule';
import { KnowledgeBaseModule } from './components/knowledge-base/KnowledgeBaseModule';
import { KernboodschapModule } from './components/kernboodschap/KernboodschapModule';
import { ProjectverkenningModule } from './components/projectverkenning/ProjectverkenningModule';
import { OmgevingsanalyseModule } from './components/omgevingsanalyse/OmgevingsanalyseModule';
import { MediaDashboardModule } from './components/omgevingsanalyse/MediaDashboardModule';
import { StakeholdersModule } from './components/omgevingsanalyse/StakeholdersModule';
import { DossierModule } from './components/dossier/DossierModule';
import { PersonasFaseModule } from './components/personas-fase/PersonasFaseModule';
import { StrategieFaseModule } from './components/strategie-fase/StrategieFaseModule';
import { WerkplaatsModule } from './components/werkplaats/WerkplaatsModule';
import { ProfilePage } from './pages/Profile/ProfilePage';

import { Module, Project, User } from './types';
import { mockProjects, mockUser, mockDashboardMetrics, mockPersonas, mockSentimentData } from './data/mockData';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { projectsAPI, dashboardAPI } from './services/api';

interface ProjectLayoutProps {
  user: User;
  projects: Project[];
  loadingProjects: boolean;
  onLogout: () => void;
}

const ProjectLayout: React.FC<ProjectLayoutProps> = ({ user, projects, loadingProjects, onLogout }) => {
  const navigate = useNavigate();
  const { projectId, module = 'dashboard' } = useParams<{ projectId: string; module?: string }>();

  // Fase-modules van de begeleide journey: hier staat de Projectflow-rail links.
  const RAIL_MODULES = ['projectverkenning', 'omgevingsanalyse', 'gebiedsanalyse', 'mediadashboard', 'stakeholders', 'personas-fase', 'strategie-fase', 'kernboodschap', 'werkplaats', 'dossier'];
  const showRail = RAIL_MODULES.includes(module);
  const isStart = module === 'dashboard' || module === 'flow';

  const currentProject = projects.find(p => p.id === projectId);

  if (loadingProjects) {
    return <div className={styles.loading}>Laden...</div>;
  }

  if (!currentProject) {
    return <Navigate to="/projects" />;
  }

  const handleProjectChange = (project: Project) => {
    // Projecttoggle gaat altijd naar het startscherm/overzicht van het gekozen project,
    // ongeacht waar je nu zit.
    navigate(`/project/${project.id}/dashboard`);
  };

  const handleAllProjectsClick = () => {
    navigate('/projects');
  };

  const handleModuleChange = (newModule: Module) => {
    navigate(`/project/${projectId}/${newModule}`);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <div className={styles.app}>
      <Header
        currentProject={currentProject}
        projects={projects}
        user={user}
        onProjectChange={handleProjectChange}
        onAllProjectsClick={handleAllProjectsClick}
        onLogout={handleLogout}
      />
      
      <div className={styles.layout}>
        {showRail ? (
          <ProjectFlowNav
            project={currentProject}
            activeModule={module}
            onNavigate={handleModuleChange as (m: string) => void}
          />
        ) : !isStart ? (
          <Sidebar
            activeModule={module as Module}
            onModuleChange={handleModuleChange}
            enabledModules={currentProject.settings?.modules}
          />
        ) : null}

        <main
          className={(module === 'gebiedsanalyse' ? styles.mainContentWide : ((isStart || showRail) ? styles.mainContentFull : styles.mainContent))}
          style={showRail && module !== 'gebiedsanalyse' ? { background: '#f4f5f7' } : undefined}
        >
          {isStart && (
            <DashboardModule
              project={currentProject}
              metrics={mockDashboardMetrics}
            />
          )}

          {module === 'gebiedsanalyse' && (
            <GebiedsanalyseModule
              project={currentProject}
            />
          )}

          {module === 'sentiment' && (
            <SentimentModule
              project={currentProject}
              sentimentData={mockSentimentData}
            />
          )}
          
          {module === 'personas' && (
            <PersonasModule
              project={currentProject}
            />
          )}
          
          {module === 'strategy' && (
            <StrategyModule
              project={currentProject}
            />
          )}
          
          {module === 'media' && (
            <MiddelenModule
              project={currentProject}
            />
          )}
          
          {module === 'werkplanning' && (
            <WerkplanningModule project={currentProject} />
          )}

          {module === 'vragen' && (
            <VragenModule
              project={currentProject}
            />
          )}

          {module === 'relatiebeheer' && (
            <RelatiebeheerModule
              project={currentProject}
            />
          )}

          {module === 'projectwebsite' && (
            <ProjectWebsiteModule
              project={currentProject}
            />
          )}

          {module === 'issue-radar' && (
            <IssueRadarModule
              project={currentProject}
            />
          )}

          {module === 'participatie' && (
            <ParticipationModule
              project={currentProject}
            />
          )}

          {module === 'rapportage' && (
            <ReportsModule
              project={currentProject}
            />
          )}

          {module === 'stakeholder-netwerk' && (
            <StakeholderNetworkModule
              project={currentProject}
            />
          )}

          {module === 'vergunningen' && (
            <PermitsModule
              project={currentProject}
            />
          )}

          {module === 'kennisbank' && (
            <KnowledgeBaseModule
              project={currentProject}
            />
          )}

          {module === 'kernboodschap' && (
            <KernboodschapModule
              project={currentProject}
            />
          )}

          {module === 'projectverkenning' && (
            <ProjectverkenningModule
              project={currentProject}
            />
          )}

          {module === 'omgevingsanalyse' && (
            <OmgevingsanalyseModule
              project={currentProject}
            />
          )}

          {module === 'mediadashboard' && (
            <MediaDashboardModule project={currentProject} />
          )}

          {module === 'stakeholders' && (
            <StakeholdersModule project={currentProject} />
          )}

          {module === 'werkplaats' && (
            <WerkplaatsModule project={currentProject} />
          )}

          {module === 'dossier' && (
            <DossierModule project={currentProject} />
          )}

          {module === 'personas-fase' && (
            <PersonasFaseModule
              project={currentProject}
            />
          )}

          {module === 'strategie-fase' && (
            <StrategieFaseModule
              project={currentProject}
            />
          )}
        </main>
      </div>
    </div>
  );
};

const ProjectSelectionRouter: React.FC<{
  projects: Project[];
  user: User;
  isAdmin: boolean;
  onLogout: () => void;
}> = ({ projects, user, isAdmin, onLogout }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (projects.length === 1 && !isAdmin) {
      navigate(`/project/${projects[0].id}/dashboard`, { replace: true });
    }
  }, [projects, isAdmin, navigate]);

  if (projects.length === 1 && !isAdmin) {
    return null;
  }

  return (
    <ProjectSelection
      projects={projects}
      user={user}
      isAdmin={isAdmin}
      onProjectSelect={() => {}}
      onLogout={onLogout}
    />
  );
};

function AppContent() {
  const { user, login, loginWithLinkedIn, logout, isAuthenticated, isAdmin, loading } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProjects();
    }
  }, [isAuthenticated]);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const projectsData = await projectsAPI.getMyProjects();
      // Map backend projects to frontend Project type
      const mappedProjects = projectsData.map((p: any) => ({
        id: p._id,
        name: p.name,
        description: p.description,
        shortDescription: p.shortDescription || '',
        location: p.location?.city || p.location || 'Unknown',
        locationData: p.location || null,
        startDate: p.startDate,
        endDate: p.endDate,
        status: p.status,
        headerImage: p.headerImage,
        company: p.company,
        settings: p.settings,
        metrics: p.metrics || {
          sentimentScore: 0,
          stakeholderCount: 0,
          mediaReach: 0,
          engagementRate: 0
        }
      }));
      setProjects(mappedProjects);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      // Fall back to mock data if API fails
      setProjects(mockProjects);
    } finally {
      setLoadingProjects(false);
    }
  };

  const toast = useToast();

  const handleLogin = async (email: string, password: string, remember: boolean = true) => {
    const result = await login(email, password, remember);
    if (!result.success) {
      toast.error('Inloggen mislukt', result.error || 'Controleer je e-mailadres en wachtwoord.');
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ?
            <Navigate to="/projects" /> :
            <Login onLogin={handleLogin} onLinkedInLogin={loginWithLinkedIn} />
        }
      />

      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      <Route
        path="/projects"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            loadingProjects ?
              <div className={styles.loading}>Projecten laden...</div> :
              <ProjectSelectionRouter
                projects={projects}
                user={user!}
                isAdmin={isAdmin}
                onLogout={logout}
              />
        }
      />

      <Route
        path="/profile"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            <ProfilePage />
        }
      />

      {/* /settings is samengevoegd met /profile */}
      <Route path="/settings" element={<Navigate to="/profile" replace />} />

      <Route
        path="/admin"
        element={
          !isAuthenticated || !isAdmin ?
            <Navigate to="/login" /> :
            <AdminLayout
              user={user!}
              onLogout={logout}
            />
        }
      >
        <Route index element={<Navigate to="/admin/projects" replace />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/new" element={<CompanyEditPage />} />
        <Route path="companies/:companyId" element={<CompanyDetailPage />} />
        <Route path="companies/:companyId/edit" element={<CompanyEditPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={<ProjectEditPage />} />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="projects/:projectId/edit" element={<ProjectEditPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/new" element={<UserFormPage />} />
        <Route path="users/:userId/edit" element={<UserFormPage />} />
        <Route path="datasources" element={<DataSourcesPage />} />
        <Route path="ai-usage" element={<AiUsagePage />} />
        <Route path="prompts" element={<PromptsPage />} />
      </Route>

      <Route
        path="/project/:projectId/:module/*"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            <ProjectLayout
              user={user!}
              projects={projects}
              loadingProjects={loadingProjects}
              onLogout={logout}
            />
        }
      />
      <Route
        path="/project/:projectId"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            <ProjectLayout
              user={user!}
              projects={projects}
              loadingProjects={loadingProjects}
              onLogout={logout}
            />
        }
      />

      <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />

      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <ToastProvider>
        <Routes>
          {/* Public site route — no auth required */}
          <Route path="/site/:slug" element={<PublicSiteLayout />} />

          {/* All authenticated routes */}
          <Route path="/*" element={
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          } />
        </Routes>
      </ToastProvider>
    </Router>
  );
}

export default App;